import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TagDialog } from "@/components/chat/TagDialog";
import { MessageSquare, MoreHorizontal, Pin, PinOff, Pencil, Trash2, Search, X, Tag } from "lucide-react";

function RenameInput({ conv, onDone }) {
  const [value, setValue] = useState(conv.title || "");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const save = () => onDone(value.trim() && value.trim() !== conv.title ? value.trim() : null);
  return (
    <input ref={ref} value={value} maxLength={80} data-testid={`conv-rename-input-${conv.id}`}
      onChange={(e) => setValue(e.target.value)} onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onDone(null); }}
      className="flex-1 min-w-0 text-sm bg-white border border-orange rounded-lg px-2 py-1 outline-none text-navy" />
  );
}

function ConvRow({ c, active, renaming, onOpen, onMenu, onRenamed }) {
  return (
    <div className={`group relative flex items-start rounded-xl transition-colors ${active ? "bg-orange/10" : "hover:bg-secondary"}`}>
      {renaming ? (
        <div className="flex-1 flex items-center gap-2 p-2.5"><MessageSquare className="h-4 w-4 text-orange shrink-0" /><RenameInput conv={c} onDone={onRenamed} /></div>
      ) : (
        <button data-testid={`conv-${c.id}`} onClick={() => onOpen(c.id)} className="flex-1 min-w-0 text-left flex items-start gap-2 p-3 pr-10 text-navy">
          {c.pinned ? <Pin className="h-4 w-4 text-orange mt-0.5 shrink-0" fill="currentColor" /> : <MessageSquare className="h-4 w-4 text-orange mt-0.5 shrink-0" />}
          <span className="min-w-0">
            <span className="text-sm line-clamp-2">{c.title || "Conversation"}</span>
            {c.tags?.length > 0 && (
              <span className="flex flex-wrap gap-1 mt-1" data-testid={`conv-tags-${c.id}`}>
                {c.tags.map((t) => <span key={t.external_id} className="text-[10px] font-semibold bg-navy/5 text-navy px-1.5 py-0.5 rounded-md truncate max-w-[140px]">{t.title}</span>)}
              </span>
            )}
            {c.snippet && <span className="block text-[11px] text-muted-foreground line-clamp-2 mt-0.5" data-testid={`conv-snippet-${c.id}`}>{c.snippet}</span>}
          </span>
        </button>
      )}
      {!renaming && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`conv-menu-${c.id}`} aria-label="Conversation actions"
              className="absolute right-2 top-2.5 h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground opacity-60 md:opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100 hover:bg-white transition-opacity">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem data-testid={`conv-pin-${c.id}`} onClick={() => onMenu("pin", c)}>
              {c.pinned ? <><PinOff className="h-4 w-4 mr-2" /> Unpin</> : <><Pin className="h-4 w-4 mr-2" /> Pin</>}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`conv-tag-${c.id}`} onClick={() => onMenu("tag", c)}><Tag className="h-4 w-4 mr-2" /> Tags</DropdownMenuItem>
            <DropdownMenuItem data-testid={`conv-rename-${c.id}`} onClick={() => onMenu("rename", c)}><Pencil className="h-4 w-4 mr-2" /> Rename</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`conv-delete-${c.id}`} onClick={() => onMenu("delete", c)} className="text-red-600 focus:text-red-700"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function DeleteDialog({ conv, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!conv} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="conv-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
          <AlertDialogDescription>"{conv?.title || "Conversation"}" will be permanently removed from your history.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="conv-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="conv-delete-confirm" onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ConversationRail({ agentParam, activeId, reloadKey, onOpen, onActiveDeleted }) {
  const [convs, setConvs] = useState([]);
  const [q, setQ] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [tagging, setTagging] = useState(null);
  const [tags, setTags] = useState([]);
  const [tag, setTag] = useState("all");

  const load = useCallback(() => {
    const params = { agent: agentParam, q: q.trim() || undefined, tag: tag !== "all" ? tag : undefined };
    api.get("/chat/conversations", { params }).then((r) => setConvs(r.data)).catch(() => {});
    api.get("/chat/tags", { params: { agent: agentParam } }).then((r) => setTags(r.data)).catch(() => {});
  }, [agentParam, q, tag]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q, reloadKey]);
  useEffect(() => { if (tag !== "all" && tags.length && !tags.some((t) => t.external_id === tag)) setTag("all"); }, [tags, tag]);

  const patch = async (c, body, msg) => {
    try { await api.patch(`/chat/conversations/${c.id}`, body); toast.success(msg); load(); }
    catch { toast.error("Could not update the conversation"); }
  };
  const onMenu = (action, c) => {
    if (action === "pin") patch(c, { pinned: !c.pinned }, c.pinned ? "Conversation unpinned" : "Conversation pinned");
    else if (action === "rename") setTimeout(() => setRenamingId(c.id), 50);
    else if (action === "tag") setTimeout(() => setTagging(c), 50);
    else setToDelete(c);
  };
  const confirmDelete = async () => {
    const c = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/chat/conversations/${c.id}`);
      setConvs((cs) => cs.filter((x) => x.id !== c.id));
      if (activeId === c.id) onActiveDeleted();
      toast.success("Conversation deleted");
    } catch { toast.error("Could not delete the conversation"); }
  };

  const pinned = convs.filter((c) => c.pinned);
  const others = convs.filter((c) => !c.pinned);
  const row = (c) => (
    <ConvRow key={c.id} c={c} active={activeId === c.id} renaming={renamingId === c.id} onOpen={onOpen} onMenu={onMenu}
      onRenamed={(title) => { setRenamingId(null); if (title) patch(c, { title }, "Conversation renamed"); }} />
  );
  const label = (text) => <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{text}</div>;

  return (
    <>
      <div className="px-3 pt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input data-testid="conv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations…" className="h-9 pl-8 pr-8 rounded-xl text-sm" />
          {q && <button data-testid="conv-search-clear" onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy"><X className="h-3.5 w-3.5" /></button>}
        </div>
        {tags.length > 0 && (
          <Select value={tag} onValueChange={setTag}>
            <SelectTrigger className="h-9 mt-2 rounded-xl text-xs" data-testid="conv-tag-filter"><Tag className="h-3.5 w-3.5 mr-1 text-orange" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="conv-tag-filter-all">All tags</SelectItem>
              {tags.map((t) => <SelectItem key={t.external_id} value={t.external_id} data-testid={`conv-tag-filter-${t.external_id}`}>{t.title} ({t.count})</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="conv-list">
        {convs.length === 0 && <p className="text-sm text-muted-foreground px-2 py-3" data-testid="conv-empty">{q || tag !== "all" ? "No conversation matches your search." : "No conversations yet."}</p>}
        {pinned.length > 0 && <div data-testid="conv-pinned-section">{label("Pinned")}{pinned.map(row)}</div>}
        {pinned.length > 0 && others.length > 0 && label("Recent")}
        {others.map(row)}
      </div>
      <TagDialog conv={tagging} onClose={() => setTagging(null)}
        onSave={(ids) => { const c = tagging; setTagging(null); patch(c, { tags: ids }, "Tags updated"); }} />
      <DeleteDialog conv={toDelete} onCancel={() => setToDelete(null)} onConfirm={confirmDelete} />
    </>
  );
}
